import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Columns2, Loader2, ClipboardPaste } from "lucide-react";

// AB-Tool: Screenshot des Inhaltsverzeichnisses einfügen → Vision-Analyse → Einträge.
export default function AbToolDialog({ docId, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const setImg = (f) => {
    if (!f || !f.type?.startsWith("image/")) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const reset = () => { setFile(null); setPreview(""); };
  const setOpenState = (v) => { setOpen(v); if (!v) reset(); };

  // Strg+V: Bild aus der Zwischenablage übernehmen (solange der Dialog offen ist).
  useEffect(() => {
    if (!open) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) { setImg(it.getAsFile()); e.preventDefault(); break; }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open]);

  const analyze = async () => {
    if (!file) { toast.error("Bitte zuerst einen Screenshot einfügen oder ein Bild wählen"); return; }
    const fd = new FormData();
    fd.append("image", file);
    setBusy(true);
    try {
      const r = await api.post(`/documents/${docId}/abtool`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`AB-Tool: ${(r.data.entries || []).length - 1} Einträge erkannt`);
      setOpenState(false);
      onDone && onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AB-Tool fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
        className="border-[#0F4C81]/40 text-[#0F4C81] hover:bg-[#0F4C81]/10" data-testid="editor-abtool"
        title="Screenshot des Inhaltsverzeichnisses einfügen und analysieren">
        <Columns2 className="h-4 w-4 mr-1" /> AB-Tool
      </Button>

      <Dialog open={open} onOpenChange={setOpenState}>
        <DialogContent className="sm:max-w-xl" data-testid="abtool-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">AB-Tool — Inhaltsverzeichnis per Screenshot</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            Kopiere einen Screenshot des Inhaltsverzeichnis-Bereichs und füge ihn hier mit
            <b> Strg + V</b> ein (oder wähle ein Bild). Es werden nur Einträge <b>mit Seitenzahl</b>
            (Punkte „…" davor) erstellt.
          </p>

          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => setImg(e.target.files?.[0])} data-testid="abtool-file-input" />

          {preview ? (
            <div className="space-y-2">
              <img src={preview} alt="Vorschau" data-testid="abtool-preview"
                className="max-h-72 w-full object-contain rounded-md border border-gray-300 bg-gray-50" />
              <Button variant="ghost" size="sm" onClick={reset} data-testid="abtool-clear" title="Anderes Bild wählen">Anderes Bild</Button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="abtool-paste-area" title="Bild auswählen oder mit Strg+V einfügen"
              className="w-full rounded-lg border-2 border-dashed border-gray-300 hover:border-[#0F4C81] hover:bg-[#0F4C81]/5 px-4 py-10 text-center text-sm text-gray-500 transition-colors">
              <ClipboardPaste className="h-6 w-6 mx-auto mb-2" />
              Screenshot hier einfügen (Strg + V) oder Bild auswählen
            </button>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenState(false)} data-testid="abtool-cancel" title="Abbrechen">Abbrechen</Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0d3f6b] text-white" onClick={analyze} disabled={!file || busy} data-testid="abtool-analyze" title="Screenshot analysieren">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Columns2 className="h-4 w-4 mr-1.5" />} Analysieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
